<script setup lang="ts">
import MainPresentation from '@/components/showcase/MainPresentation.vue';
import Reviews from '@/components/basic/Reviews/Reviews.vue';
import useSeo from '@/composables/useSeo';
import {getPricing} from '@/modules/pricing';

const price = getPricing();

const setupOptions = [
    {
        label: 'JavaScript',
        title: 'NPM or CDN',
        text: 'Add the viewer to any frontend and keep full control over images, hosting and behaviour.',
        to: '/installation',
        link: 'Open the installation tool'
    },
    {
        label: 'WordPress',
        title: 'WooCommerce plugin',
        text: 'Create product spins from the WordPress dashboard without building a custom integration.',
        to: '/wordpress',
        link: 'View the WordPress plugin'
    },
    {
        label: 'Hosted embed',
        title: 'Simple iframe',
        text: 'Configure a presentation online and place it in a webshop, landing page or CMS.',
        to: '/installation',
        link: 'Create an embed'
    }
];

const licensedBrands = [
    {name: 'STIEBEL ELTRON', domain: 'stiebel-eltron.co.za'},
    {name: 'SATEC', domain: 'satec-global.com'},
    {name: 'Ligna Energy', domain: 'lignaenergy.com'},
    {name: 'LEVA Motors', domain: 'levamotors.com'},
    {name: 'Gibson Products', domain: 'gibsonproducts.com'},
    {name: 'Summit Radio', domain: 'summitradio.co.nz'}
];

useSeo({
    title: '360 Product Viewer for Webshops',
    description: 'Add interactive 360° product spins to your webshop. NPM package, WordPress plugin, CDN support, and one-time pricing with 360 Javascript Viewer.'
});
</script>

<template>
    <SectionElement light class="home-hero">
        <div class="page-shell hero-layout">
            <div class="hero-copy">
                <p class="section-kicker">360° JavaScript product viewer</p>
                <h1>Add interactive 360° product views to any webshop.</h1>
                <p class="hero-lead">
                    Use your own product images and hosting. Install with JavaScript, CDN or WordPress,
                    then keep the free badge or remove it with one lifetime license.
                </p>
                <div class="hero-actions">
                    <RouterLink class="btn btn-primary cta-primary" to="/installation">Build your viewer</RouterLink>
                    <RouterLink class="btn btn-outline-secondary" to="/pricing">Lifetime license · {{ price }}</RouterLink>
                </div>
                <ul class="hero-details" aria-label="Product benefits">
                    <li>No subscription</li>
                    <li>Unlimited views</li>
                    <li>Your own hosting</li>
                </ul>
            </div>

            <div class="media-panel viewer-panel">
                <client-only>
                    <MainPresentation />
                </client-only>
                <p>Drag to rotate · Real product frames</p>
            </div>
        </div>
    </SectionElement>

    <section class="proof-band" aria-label="Product usage">
        <div class="page-shell proof-layout">
            <div>
                <strong>21M+</strong>
                <span>CDN requests / quarter</span>
            </div>
            <div>
                <strong>1,000+</strong>
                <span>active WordPress installs</span>
            </div>
            <div>
                <strong>2–365</strong>
                <span>images per product view</span>
            </div>
        </div>
    </section>

    <SectionElement light class="customer-proof-section">
        <div class="page-shell">
            <div class="customer-proof-heading">
                <div>
                    <p class="section-kicker">Customer examples</p>
                    <h2>Built for teams that need product views to simply work</h2>
                </div>
                <p>
                    <strong>Used by product teams around the world.</strong> Our domain licenses support webshops,
                    manufacturers and agencies across Europe, the Americas, Asia-Pacific and the Middle East.
                </p>
            </div>

            <div class="licensed-brands" aria-label="Examples of licensed customer domains">
                <div v-for="brand in licensedBrands" :key="brand.domain" class="brand-name">
                    <strong>{{ brand.name }}</strong>
                    <span>{{ brand.domain }}</span>
                </div>
            </div>

            <div class="review-heading">
                <p class="section-kicker">What WordPress users say</p>
                <p>Independent reviews link directly to their original posts on WordPress.org.</p>
            </div>
            <Reviews />
            <p class="license-source-note">Customer examples are domains for which paid licenses were issued through our checkout.</p>
        </div>
    </SectionElement>

    <SectionElement light class="setup-section">
        <div class="page-shell">
            <div class="section-heading">
                <p class="section-kicker">Use the stack you already have</p>
                <h2>Three straightforward ways to start</h2>
                <p>No proprietary image hosting and no platform lock-in.</p>
            </div>

            <div class="setup-grid">
                <article v-for="option in setupOptions" :key="option.title" class="setup-item">
                    <p class="setup-label">{{ option.label }}</p>
                    <h3>{{ option.title }}</h3>
                    <p>{{ option.text }}</p>
                    <RouterLink :to="option.to">{{ option.link }} <span aria-hidden="true">→</span></RouterLink>
                </article>
            </div>
        </div>
    </SectionElement>

    <SectionElement light class="license-section">
        <div class="page-shell license-layout">
            <div class="license-copy">
                <p class="section-kicker">Simple pricing</p>
                <h2>Start free. Pay once when you want your own branding.</h2>
                <p>
                    Every viewer feature is available in the free version. The lifetime license removes our
                    badge from one production domain—without monthly fees or view limits.
                </p>
                <blockquote>
                    “Easy to use, feature-rich, and great results. Support was top-tier and very fast.”
                    <cite>— mastrognappo on WordPress.org</cite>
                </blockquote>
            </div>

            <aside class="license-summary">
                <p>Lifetime domain license</p>
                <div class="price-row">
                    <strong>{{ price }}</strong>
                    <span>one-time</span>
                </div>
                <ul>
                    <li>One production domain</li>
                    <li>Matching www and staging included</li>
                    <li>Email support</li>
                </ul>
                <BuyButton label="Remove the branding" location="homepage-license" />
                <small>Secure checkout by PayPro Global · Local tax may be added</small>
            </aside>
        </div>
    </SectionElement>
</template>

<style scoped lang="scss">
.home-hero { padding-bottom: clamp(2rem, 6vw, 5rem); padding-top: clamp(2.5rem, 6vw, 5rem); }
.license-summary > p {
    color: var(--brand-secondary);
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    margin: 0 0 0.7rem;
    text-transform: uppercase;
}

.viewer-panel {
    min-height: 29rem;
}

.viewer-panel > p {
    bottom: 1.2rem;
    color: var(--brand-muted-text);
    font-size: 0.82rem;
    left: 0;
    margin: 0;
    position: absolute;
    text-align: center;
    width: 100%;
}

.customer-proof-section { padding-bottom: clamp(3rem, 7vw, 5.5rem); padding-top: clamp(3rem, 7vw, 5.5rem); }
.customer-proof-heading { align-items: end; display: grid; gap: 2rem; grid-template-columns: minmax(0, 1.2fr) minmax(18rem, 0.8fr); }
.customer-proof-heading h2 { font-size: clamp(2rem, 4vw, 3rem); line-height: 1.15; margin: 0; max-width: 20ch; }
.customer-proof-heading > p { color: var(--brand-muted-text); line-height: 1.65; margin: 0; }
.customer-proof-heading > p strong { color: var(--brand-heading); }

.licensed-brands {
    border-bottom: 1px solid #dedede;
    border-top: 1px solid #dedede;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 2.2rem 0 3rem;
}

.brand-name { display: flex; flex-direction: column; gap: 0.2rem; padding: 1.2rem 1.4rem; }
.brand-name:nth-child(3n + 2),
.brand-name:nth-child(3n + 3) { border-left: 1px solid #dedede; }
.brand-name:nth-child(n + 4) { border-top: 1px solid #dedede; }
.brand-name strong { color: var(--brand-heading); font-family: Inter, sans-serif; font-size: 1rem; }
.brand-name span { color: var(--brand-muted-text); font-size: 0.78rem; }

.review-heading { align-items: baseline; display: flex; gap: 1rem; justify-content: space-between; margin-bottom: 1.1rem; }
.review-heading p { margin-bottom: 0; }
.review-heading > p:last-child { color: var(--brand-muted-text); font-size: 0.85rem; }
.license-source-note { color: var(--brand-muted-text); font-size: 0.72rem; margin: 0.6rem 0 0; }

.setup-section { padding-bottom: clamp(3rem, 7vw, 5.5rem); padding-top: clamp(3rem, 7vw, 5.5rem); }
.license-copy h2 { font-size: clamp(2rem, 4vw, 3rem); line-height: 1.15; margin: 0; }
.setup-item > p:not(.setup-label) { min-height: 4.8rem; }

.license-section { background: var(--brand-bg-lightest) !important; padding-bottom: clamp(3rem, 7vw, 5.5rem); padding-top: clamp(3rem, 7vw, 5.5rem); }
.license-layout { align-items: center; display: grid; gap: clamp(2.5rem, 8vw, 7rem); grid-template-columns: minmax(0, 1.2fr) minmax(19rem, 0.8fr); }
.license-copy > p:not(.section-kicker) { line-height: 1.7; max-width: 42rem; }

blockquote {
    border-left: 3px solid var(--brand-secondary);
    color: var(--brand-heading);
    font-size: 1rem;
    font-weight: 700;
    margin: 2rem 0 0;
    max-width: 40rem;
    padding-left: 1rem;
}

blockquote cite { color: var(--brand-muted-text); display: block; font-size: 0.82rem; font-style: normal; font-weight: 400; margin-top: 0.45rem; }

.license-summary { background: #fff; border: 1px solid #d5d5d5; border-radius: 0.75rem; padding: 1.75rem; }
.price-row { align-items: baseline; display: flex; gap: 0.65rem; }
.price-row strong { color: var(--brand-heading); font-family: Inter, sans-serif; font-size: 3.25rem; line-height: 1; }
.price-row span { color: var(--brand-muted-text); }
.license-summary ul { list-style: none; margin: 1.4rem 0; padding: 0; }
.license-summary li { padding-left: 1.3rem; position: relative; }
.license-summary li + li { margin-top: 0.55rem; }
.license-summary li::before { color: var(--brand-secondary); content: '✓'; font-weight: 700; left: 0; position: absolute; }
.license-summary :deep(.btn) { justify-content: center; width: 100%; }
.license-summary small { color: var(--brand-muted-text); display: block; font-size: 0.75rem; margin-top: 0.7rem; text-align: center; }

@media (max-width: 900px) {
    .license-layout,
    .customer-proof-heading { grid-template-columns: 1fr; }
    .viewer-panel { min-height: 25rem; }
}

@media (max-width: 700px) {
    .home-hero { padding-top: 2.2rem; }
    .hero-copy h1 { max-width: none; }
    .viewer-panel { min-height: 23rem; padding: 0.5rem; }
    .licensed-brands { grid-template-columns: 1fr; }
    .setup-item > p:not(.setup-label) { min-height: 0; }
    .brand-name:nth-child(n) { border-left: 0; }
    .brand-name + .brand-name { border-top: 1px solid #dedede; }
    .review-heading { align-items: flex-start; flex-direction: column; gap: 0.25rem; }
}
</style>
